import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  MailOpen, 
  MousePointerClick, 
  Send,
  CheckCircle,
  XCircle,
  TrendingUp,
  RefreshCw,
  Eye
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/toast";
import { 
  getTrackingStats, 
  getCampaigns,
  getCampaignTrackingStats,
  getEmailEvents
} from "@/lib/marketing-api";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from "recharts";

export default function Tracking() {
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignStats, setCampaignStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, campaignsRes, eventsRes] = await Promise.all([
        getTrackingStats(),
        getCampaigns(),
        getEmailEvents({ limit: 20 })
      ]);
      setStats(statsRes.data);
      setCampaigns(campaignsRes.data.filter(c => c.channel === 'email'));
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const loadCampaignStats = async (campaignId) => {
    if (!campaignId) {
      setCampaignStats(null);
      setSelectedCampaign(null);
      return;
    }
    try {
      const response = await getCampaignTrackingStats(campaignId);
      setCampaignStats(response.data);
      setSelectedCampaign(campaignId);
    } catch (error) {
      toast.error(t('errors.serverError'));
    }
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'email.sent': return <Send className="w-4 h-4 text-blue-400" />;
      case 'email.delivered': return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'email.opened': return <MailOpen className="w-4 h-4 text-purple-400" />;
      case 'email.clicked': return <MousePointerClick className="w-4 h-4 text-orange-400" />;
      case 'email.bounced': return <XCircle className="w-4 h-4 text-red-400" />;
      default: return <Send className="w-4 h-4" />;
    }
  };

  const getEventLabel = (type) => {
    switch (type) {
      case 'email.sent': return t('marketingFull.sent');
      case 'email.delivered': return t('marketingFull.delivered');
      case 'email.opened': return t('marketingFull.opened');
      case 'email.clicked': return t('marketingFull.clicked');
      case 'email.bounced': return t('marketingFull.bounced');
      default: return type;
    }
  };

  // Chart data
  const funnelData = stats ? [
    { name: t('marketingFull.sent'), value: stats.total_sent, fill: '#3B82F6' },
    { name: t('marketingFull.delivered'), value: stats.total_delivered, fill: '#22C55E' },
    { name: t('marketingFull.opened'), value: stats.total_opened, fill: '#A855F7' },
    { name: t('marketingFull.clicked'), value: stats.total_clicked, fill: '#F97316' },
  ] : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="tracking-page">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-black text-foreground">{t('marketingFull.trackingTitle')}</h1>
          <p className="text-muted-foreground mt-1">{t('marketingFull.trackingSubtitle')}</p>
        </div>
        <Button variant="outline" onClick={loadData} data-testid="refresh-tracking-btn">
          <RefreshCw className="w-4 h-4 me-2" />
          {t('marketingFull.refresh')}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="stat-card" data-testid="stat-sent">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Send className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('marketingFull.totalSent')}</p>
                <p className="text-2xl font-black font-numbers">{stats?.total_sent || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-delivered">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('marketingFull.totalDelivered')}</p>
                <p className="text-2xl font-black font-numbers">{stats?.total_delivered || 0}</p>
                <p className="text-xs text-green-400 font-numbers">{stats?.delivery_rate || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-opened">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <MailOpen className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('marketingFull.totalOpened')}</p>
                <p className="text-2xl font-black font-numbers">{stats?.total_opened || 0}</p>
                <p className="text-xs text-purple-400 font-numbers">{stats?.open_rate || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-clicked">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <MousePointerClick className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('marketingFull.totalClicked')}</p>
                <p className="text-2xl font-black font-numbers">{stats?.total_clicked || 0}</p>
                <p className="text-xs text-orange-400 font-numbers">{stats?.click_rate || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Funnel Chart */}
        <Card data-testid="funnel-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              {t('marketingFull.conversionFunnel')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnelData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis type="number" stroke="#64748B" fontSize={12} />
                  <YAxis dataKey="name" type="category" stroke="#64748B" fontSize={12} width={80} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: "#1E293B", 
                      border: "1px solid #334155",
                      borderRadius: "8px",
                      direction: isRTL ? "rtl" : "ltr"
                    }}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Campaign Stats */}
        <Card data-testid="campaign-stats">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-primary" />
              {t('marketingFull.campaignStats')}
            </CardTitle>
            <CardDescription>
              {t('marketingFull.selectCampaignForStats')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={selectedCampaign || ""} onValueChange={loadCampaignStats}>
              <SelectTrigger data-testid="campaign-select">
                <SelectValue placeholder={t('marketingFull.selectCampaign')} />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map(campaign => (
                  <SelectItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {campaignStats && (
              <div className="mt-6 space-y-4">
                <h4 className="font-bold">{campaignStats.campaign_name}</h4>
                
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{t('marketingFull.deliveryRate')}</span>
                      <span className="font-numbers">{campaignStats.delivery_rate}%</span>
                    </div>
                    <Progress value={campaignStats.delivery_rate} className="bg-muted" />
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{t('marketingFull.openRate')}</span>
                      <span className="font-numbers">{campaignStats.open_rate}%</span>
                    </div>
                    <Progress value={campaignStats.open_rate} className="bg-muted" />
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{t('marketingFull.clickRate')}</span>
                      <span className="font-numbers">{campaignStats.click_rate}%</span>
                    </div>
                    <Progress value={campaignStats.click_rate} className="bg-muted" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                  <div className="text-center">
                    <p className="text-2xl font-black font-numbers text-green-400">{campaignStats.sent}</p>
                    <p className="text-xs text-muted-foreground">{t('marketingFull.sent')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-black font-numbers text-red-400">{campaignStats.failed}</p>
                    <p className="text-xs text-muted-foreground">{t('marketingFull.failed')}</p>
                  </div>
                </div>
              </div>
            )}

            {!campaignStats && campaigns.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Send className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>{t('marketingFull.noEmailCampaigns')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Events */}
      <Card data-testid="recent-events" className="border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10">
        <CardHeader className="bg-primary/10 border-b border-primary/20">
          <CardTitle className="text-xl font-black flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <MailOpen className="w-5 h-5 text-primary" />
            </div>
            {t('marketingFull.recentEvents')}
          </CardTitle>
          <CardDescription className="text-base">
            {t('marketingFull.recentEventsDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          {events.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MailOpen className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>{t('marketingFull.noEventsYet')}</p>
              <p className="text-xs mt-1">{t('marketingFull.eventsWillAppear')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {events.map((event, index) => (
                <div 
                  key={event.id || index} 
                  className="flex items-center gap-4 p-3 rounded-lg bg-muted/50"
                >
                  {getEventIcon(event.event_type)}
                  <div className="flex-1">
                    <Badge variant="outline">{getEventLabel(event.event_type)}</Badge>
                    <p className="text-xs text-muted-foreground font-numbers mt-1">
                      {event.email_id?.slice(0, 20)}...
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground font-numbers">
                    {new Date(event.timestamp).toLocaleString(isRTL ? "ar-SA" : "en-US")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Webhook Setup Info */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            ⚙️ {t('marketingFull.webhookSetup')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            {t('marketingFull.webhookSetupDesc')}
          </p>
          <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
            <li>{isRTL ? 'اذهب إلى' : 'Go to'} <a href="https://resend.com/webhooks" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Resend Dashboard → Webhooks</a></li>
            <li>{isRTL ? 'اضغط على "Add Webhook"' : 'Click "Add Webhook"'}</li>
            <li>{isRTL ? 'أضف الرابط:' : 'Add URL:'} <code className="bg-muted px-2 py-1 rounded text-xs">{window.location.origin}/api/webhooks/resend</code></li>
            <li>{isRTL ? 'اختر الأحداث: email.sent, email.delivered, email.opened, email.clicked, email.bounced' : 'Select events: email.sent, email.delivered, email.opened, email.clicked, email.bounced'}</li>
            <li>{isRTL ? 'فعّل Open Tracking و Click Tracking من إعدادات النطاق' : 'Enable Open Tracking and Click Tracking from domain settings'}</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
